'use client';

import { type ComponentProps } from 'react';
import { Provider, Root, Trigger, Content, Portal, Arrow } from '@radix-ui/react-tooltip';

import { cn } from '@/util/utils';

function TooltipProvider({
    delayDuration = 0,
    skipDelayDuration,
    disableHoverableContent,
    ...props
}: ComponentProps<typeof Provider>) {
    return (
        <Provider
            data-slot="tooltip-provider"
            delayDuration={delayDuration}
            skipDelayDuration={skipDelayDuration}
            disableHoverableContent={disableHoverableContent}
            {...props}
        />
    );
}

function Tooltip({
    open,
    defaultOpen,
    onOpenChange,
    delayDuration,
    disableHoverableContent,
    ...props
}: ComponentProps<typeof Root>) {
    return (
        <TooltipProvider>
            <Root
                data-slot="tooltip"
                open={open}
                defaultOpen={defaultOpen}
                onOpenChange={onOpenChange}
                delayDuration={delayDuration}
                disableHoverableContent={disableHoverableContent}
                {...props}
            />
        </TooltipProvider>
    );
}

function TooltipTrigger({ asChild, ref, ...props }: ComponentProps<typeof Trigger>) {
    return <Trigger data-slot="tooltip-trigger" asChild={asChild} ref={ref} {...props} />;
}

function TooltipContent({
    className,
    sideOffset = 0,
    children,
    forceMount,
    'aria-label': ariaLabel,
    onEscapeKeyDown,
    onPointerDownOutside,
    side,
    align,
    alignOffset,
    arrowPadding,
    avoidCollisions,
    collisionBoundary,
    collisionPadding,
    sticky,
    hideWhenDetached,
    updatePositionStrategy,
    ref,
    asChild,
    ...props
}: ComponentProps<typeof Content>) {
    return (
        <Portal>
            <Content
                data-slot="tooltip-content"
                sideOffset={sideOffset}
                forceMount={forceMount}
                aria-label={ariaLabel}
                onEscapeKeyDown={onEscapeKeyDown}
                onPointerDownOutside={onPointerDownOutside}
                side={side}
                align={align}
                alignOffset={alignOffset}
                arrowPadding={arrowPadding}
                avoidCollisions={avoidCollisions}
                collisionBoundary={collisionBoundary}
                collisionPadding={collisionPadding}
                sticky={sticky}
                hideWhenDetached={hideWhenDetached}
                updatePositionStrategy={updatePositionStrategy}
                ref={ref}
                asChild={asChild}
                className={cn(
                    'bg-primary text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit max-w-sm origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs font-normal text-balance',
                    className,
                )}
                {...props}
            >
                {children}
                <Arrow className="bg-primary fill-primary z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
            </Content>
        </Portal>
    );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
